import { makeApi, Zodios } from "@zodios/core";
import { z } from "zod";

import { useAccountContext } from "./contexts/AccountContext";
import { useUserContext } from "./contexts/UserContext";

export type AccountList = Array<Account>;
export type Account = {
  crmAccountId: string;
  entitlements?:
    | /**
     * @default null
     */
    ((Array<string> | null) | Array<Array<string> | null>)
    | undefined;
  id: string;
  isCustomer?: /**
   * @default true
   */
  boolean | undefined;
  isDemo?: /**
   * @default false
   */
  boolean | undefined;
  isParent?: /**
   * @default false
   */
  boolean | undefined;
  isReferrer?: /**
   * @default false
   */
  boolean | undefined;
  isRoot?: /**
   * @default false
   */
  boolean | undefined;
  isServiceProvider?: /**
   * @default false
   */
  boolean | undefined;
  name: string;
  parentalLineage: (string | null) | Array<string | null>;
  portalStub: (string | null) | Array<string | null>;
  portalUrl: (string | null) | Array<string | null>;
  providerLineage: (string | null) | Array<string | null>;
  referralLineage: (string | null) | Array<string | null>;
  serviceProviderId: (string | null) | Array<string | null>;
  serviceProviderName: (string | null) | Array<string | null>;
  serviceProviderStub: (string | null) | Array<string | null>;
};
export type Alert = {
  ack_by: string | null;
  ack_comment: string | null;
  ack_date: string | null;
  alert_source: string | null;
  alert_time: string | null;
  alert_type: AlertType;
  clear_date: string | null;
  created_date: string;
  detail: string | null;
  entity: string | null;
  entity_description: string | null;
  entity_ip_address: string | null;
  entity_model: string | null;
  entity_type: string | null;
  external_alert_id: string | null;
  external_alert_status: string | null;
  external_alert_url: string | null;
  id: string;
  name: string;
  related_case_id: string | null;
  severity: string | null;
  state: AlertState;
  status: AlertStatus;
  threshold_date: string | null;
  trigger_date: string | null;
};
export type AlertType =
  /**
   * @enum security, operations, undefined, test
   */
  "security" | "operations" | "undefined" | "test";
export type AlertState =
  /**
   * @enum active, inactive, in_progress, closed
   */
  "active" | "inactive" | "in_progress" | "closed";
export type AlertStatus =
  /**
   * @enum new, on_hold, clear, canceled, in_progress_case_created
   */
  "new" | "on_hold" | "clear" | "canceled" | "in_progress_case_created";
export type AlertActivityList = Array<AlertActivity>;
export type AlertActivity = {
  activityType: string;
  body: string;
  createdDate: string;
  id: string;
  sender: (string | null) | Array<string | null>;
  subject: string;
};
export type AlertEmailParseRule = {
  alert_time_format?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  alert_time_regex?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  alert_type: AlertType;
  alert_url_has_alert_id: boolean;
  alert_url_regex: string;
  enabled: boolean;
  id: string;
  source_email: string;
  source_name: string;
};
export type AlertEmailParseRuleList = Array<AlertEmailParseRule>;
export type AlertList = Array<Alert>;
export type ApiCredential = {
  clientId: string;
  name: string;
  portalRole: PortalRole;
};
export type PortalRole =
  /**
   * Temp
   *
   * @enum global_super_admin, global_admin, admin, support, sales, demo, user
   */
  | "global_super_admin"
  | "global_admin"
  | "admin"
  | "support"
  | "sales"
  | "demo"
  | "user";
export type ApiCredentialList = Array<ApiCredential>;
export type AttachQueueToItemBody = {
  item_id: string;
  item_type: CRMItemType;
  queue_stub: string;
};
export type CRMItemType =
  /**
   * @enum alert, case
   */
  "alert" | "case";
export type AuthUserInfoResponse = {
  account: UserInfoAccount;
  email: (string | null) | Array<string | null>;
  email_verified: boolean;
  family_name: string;
  given_name: string;
  name: string;
  portal_url: string;
  service_provider: UserInfoServiceProvider;
  sub: string;
  updated_at?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  whitedog_app_metadata: LegacyMetadataRoot;
};
export type UserInfoAccount = {
  id: string;
  is_customer: boolean;
  is_parent: boolean;
  is_referrer: boolean;
  is_root: boolean;
  is_service_provider: boolean;
  name: string;
  role: string;
};
export type UserInfoServiceProvider = {
  account_id: (string | null) | Array<string | null>;
  account_name: (string | null) | Array<string | null>;
};
export type LegacyMetadataRoot = {
  user_info: LegacyMetadataUserInfo;
};
export type LegacyMetadataUserInfo = {
  account: UserInfoAccount;
  contact_id: string;
  gimp: boolean;
  id: string;
  portalUrl: string;
  role: string;
  service_provider: UserInfoServiceProvider;
};
export type BarChartWidgetConfigration = {
  bars: Array<BarChartWidgetBar>;
  showGrid: (boolean | null) | Array<boolean | null>;
  showLegend: (boolean | null) | Array<boolean | null>;
  showTooltip: (boolean | null) | Array<boolean | null>;
  useUniformBarColor: (boolean | null) | Array<boolean | null>;
  xAxisLabelKey: (string | null) | Array<string | null>;
  yAxisLabelKey: (string | null) | Array<string | null>;
};
export type BarChartWidgetBar = {
  colorKey: (string | null) | Array<string | null>;
  dataKey: string;
  label: (string | null) | Array<string | null>;
};
export type CRMWebhookBody = {
  object_id: string;
  object_type: CRMTypeEnum;
  org_id: string;
  parent_object_id: (string | null) | Array<string | null>;
  parent_object_type: (CRMTypeEnum | null) | Array<CRMTypeEnum | null>;
};
export type CRMTypeEnum =
  /**
   * @enum alert, incident, activity
   */
  "alert" | "incident" | "activity";
export type Case = {
  assignedQueueName: (string | null) | Array<string | null>;
  caseNumber: string;
  caseType: (CaseType | null) | Array<CaseType | null>;
  createdAt: string;
  createdById: (string | null) | Array<string | null>;
  createdByName: (string | null) | Array<string | null>;
  createdOnBehalfOfId: (string | null) | Array<string | null>;
  createdOnBehalfOfName: (string | null) | Array<string | null>;
  description: (string | null) | Array<string | null>;
  id: string;
  latestActivityDate: (string | null) | Array<string | null>;
  modifiedAt: string;
  origin: (CaseOrigin | null) | Array<CaseOrigin | null>;
  priority: (CasePriority | null) | Array<CasePriority | null>;
  resolved_date: (string | null) | Array<string | null>;
  severity: (CaseSeverity | null) | Array<CaseSeverity | null>;
  state: CaseState;
  status: CaseStatus;
  /**
   * @deprecated
   */
  ticketNumber: string;
  title: string;
};
export type CaseType =
  /**
   * @enum question, problem, request, incident, quarantine
   */
  "question" | "problem" | "request" | "incident" | "quarantine";
export type CaseOrigin =
  /**
   * @enum phone, email, portal, social, form, alert, api
   */
  "phone" | "email" | "portal" | "social" | "form" | "alert" | "api";
export type CasePriority =
  /**
   * @enum urgent, high, medium, low
   */
  "urgent" | "high" | "medium" | "low";
export type CaseSeverity =
  /**
   * @enum moderate, major, critical, minor
   */
  "moderate" | "major" | "critical" | "minor";
export type CaseState =
  /**
   * @enum active, resolved, cancelled
   */
  "active" | "resolved" | "cancelled";
export type CaseStatus =
  /**
   * @enum in_progress, on_hold, waiting_on_customer, waiting_on_partner, solved, cancelled, answered, duplicate, quarantine, new, reopen, review_pending
   */
  | "in_progress"
  | "on_hold"
  | "waiting_on_customer"
  | "waiting_on_partner"
  | "solved"
  | "cancelled"
  | "answered"
  | "duplicate"
  | "quarantine"
  | "new"
  | "reopen"
  | "review_pending";
export type CaseActivityList = Array<CaseActivity>;
export type CaseActivity = {
  activityType: string;
  body: string;
  createdDate: string;
  id: string;
  sender: (string | null) | Array<string | null>;
  subject: string;
};
export type CaseDetails = {
  assignedQueueName: string | null;
  caseLog: Array<CaseLogEntry>;
  caseNumber: string;
  caseType: CaseType | null;
  createdAt: string;
  createdById: string | null;
  createdByName: string | null;
  createdOnBehalfOfId: string | null;
  createdOnBehalfOfName: string | null;
  description: string | null;
  id: string;
  latestActivityDate: string | null;
  modifiedAt: string;
  origin: CaseOrigin | null;
  priority: CasePriority | null;
  resolved_date: string | null;
  severity: CaseSeverity | null;
  state: CaseState;
  status: CaseStatus;
  /**
   * @deprecated
   */
  ticketNumber: string;
  title: string;
};
export type CaseLogEntry = {
  createdAt: string;
  createdById: string;
  createdByName: string;
  description: string;
  fromName: string;
  htmlDescription: string;
  id: string;
  modifiedAt: string;
  modifiedById: string;
  modifiedByName: string;
  subject: string;
};
export type CaseList = Array<Case>;
export type CreateAlert = {
  alert_source: string;
  alert_time: string;
  alert_type: AlertType;
  detail: string;
  entity?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  entity_description?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  entity_ip_address?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  entity_model?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  entity_type?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  external_alert_id?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  external_alert_status?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  external_alert_url?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  severity?:
    | /**
     * @default null
     */
    (| /**
         * @maxLength 100
         */
        (string | null)
        | Array<
            /**
             * @maxLength 100
             */
            string | null
          >
      )
    | undefined;
  threshold_date?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  /**
   * @maxLength 250
   */
  title: string;
  trigger_date?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
};
export type CreateAlertEmailParseRule = {
  alert_time_format?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  alert_time_regex?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  alert_type: AlertType;
  alert_url_has_alert_id: boolean;
  alert_url_regex: string;
  enabled: boolean;
  source_email: string;
  source_name: string;
};
export type CreateCase = {
  caseType: CaseType;
  /**
   * @maxLength 9000
   */
  description: string;
  origin: CaseOrigin;
  priority: CasePriority;
  requestType?:
    | /**
     * @default null
     */
    ((CaseRequestType | null) | Array<CaseRequestType | null>)
    | undefined;
  severity: CaseSeverity;
  /**
   * @maxLength 200
   */
  title: string;
};
export type CaseRequestType =
  /**
   * @enum support, quote
   */
  "support" | "quote";
export type CreateFile = {
  category: string;
  classification?:
    | /**
     * @default null
     */
    ((FileClassification | null) | Array<FileClassification | null>)
    | undefined;
  fileMetadata?:
    | /**
     * @default null
     */
    (({} | null) | Array<{} | null>)
    | undefined;
  name: string;
  pathMetadata: {};
  upsert?: /**
   * If false will return an error if the file already exists. If true will return the existing file making any changes.
   *
   * @default false
   */
  boolean | undefined;
};
export type FileClassification =
  /**
   * @enum 0, 10, 20, 30, 40
   */
  0 | 10 | 20 | 30 | 40;
export type CreateRelatedWDCEmailActivity = {
  bcc_contacts?: Array<string> | undefined;
  cc_contacts?: Array<string> | undefined;
  conv_id?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  from_contact?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  html_body: string;
  is_customer_notification?: /**
   * @default false
   */
  boolean | undefined;
  priority?: EmailPriority | undefined;
  related_item_id: string;
  related_item_type: CRMItemType;
  subject: string;
  to_addresses?: Array<string> | undefined;
  to_contacts?: Array<string> | undefined;
};
export type EmailPriority =
  /**
   * @enum low, normal, high
   */
  "low" | "normal" | "high";
export type DashboardWidget = {
  configuration:
    | (
        | PieChartWidgetConfiguration
        | BarChartWidgetConfigration
        | TableWidgetConfiguration
      )
    | Array<
        | PieChartWidgetConfiguration
        | BarChartWidgetConfigration
        | TableWidgetConfiguration
      >;
  data: (Array<{}> | null) | Array<Array<{}> | null>;
  label: string;
  type: WidgetType;
};
export type PieChartWidgetConfiguration = {
  pies: Array<PieChartWidgetPie>;
  showLegend: (boolean | null) | Array<boolean | null>;
  showTooltip: (boolean | null) | Array<boolean | null>;
};
export type PieChartWidgetPie = {
  data: Array<PieChartWidgetDataEntry>;
};
export type PieChartWidgetDataEntry = {
  color: (FrontendColor | string | null) | Array<FrontendColor | string | null>;
  label: string;
  value: number;
};
export type FrontendColor =
  /**
   * @enum critical, high, medium, low, success, warning, danger, info, nav, light, dark, primary, secondary
   */
  | "critical"
  | "high"
  | "medium"
  | "low"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "nav"
  | "light"
  | "dark"
  | "primary"
  | "secondary";
export type TableWidgetConfiguration = {
  columns: Array<DataColumn>;
};
export type DataColumn = {
  default: (boolean | null) | Array<boolean | null>;
  displayName: string;
  enableFilter: (boolean | null) | Array<boolean | null>;
  enableSort: (boolean | null) | Array<boolean | null>;
  name: string;
  sortInverted: (boolean | null) | Array<boolean | null>;
  type: DataColumnType;
  width: (number | null) | Array<number | null>;
};
export type DataColumnType =
  /**
   * @enum string, humandatetime, datetime, boolean, number, currency, percentage, mood
   */
  | "string"
  | "humandatetime"
  | "datetime"
  | "boolean"
  | "number"
  | "currency"
  | "percentage"
  | "mood";
export type WidgetType =
  /**
   * @enum pie, bar, table, scoreboard, simple_table
   */
  "pie" | "bar" | "table" | "scoreboard" | "simple_table";
export type DataPrintSystem = {
  Inspector: DataPrintSystemInspector;
  Launchpoint: DataPrintSystemLaunchpoint;
};
export type DataPrintSystemInspector = {
  Alias: string;
  ID: number;
};
export type DataPrintSystemLaunchpoint = {
  NextScheduledFor: string;
};
export type DataPrintView = {
  ViewDefinition: ViewDefinition;
};
export type ViewDefinition = {
  Views: Array<ViewDefinitionView>;
};
export type ViewDefinitionView = {
  Items: Array<ViewDefinitionViewItem>;
  Title: string;
  ViewType: (string | null) | Array<string | null>;
};
export type ViewDefinitionViewItem = {
  Columns: (Array<string> | null) | Array<Array<string> | null>;
  DefaultSorted: (Array<SortField> | null) | Array<Array<SortField> | null>;
  InitialColumns:
    | (Array<InitialColumn> | null)
    | Array<Array<InitialColumn> | null>;
  Label: string;
  Path: (string | null) | Array<string | null>;
  Rows: (Array<Array<string>> | null) | Array<Array<Array<string>> | null>;
  Type: string;
};
export type SortField = {
  desc: boolean;
  id: string;
};
export type InitialColumn = {
  name: string;
  show: boolean;
};
export type DirectoryListing = Array<(File | Folder) | Array<File | Folder>>;
export type File = {
  createdTime: string;
  fileMetadata?:
    | /**
     * @default null
     */
    ({} | null)
    | undefined;
  id: string;
  mimeType?:
    | /**
     * @default null
     */
    (string | null)
    | undefined;
  modifiedTime: string;
  name: string;
  path: string;
  pathMetadata: {};
  size?:
    | /**
     * @default null
     */
    (number | null)
    | undefined;
  totalArchiveSize: number;
  totalLiveSize: number;
  type?: /**
   * @default "file"
   */
  string | undefined;
};
export type Folder = {
  modifiedAt: string;
  name: string;
  size: number;
  totalArchiveSize: number;
  totalLiveSize: number;
  type?: /**
   * @default "folder"
   */
  string | undefined;
};
export type EmailTemplateList = Array<EmailTemplateListItem>;
export type EmailTemplateListItem = {
  created_on: string;
  name: string;
};
export type FailedEmailQueueItem = {
  created_at: string;
  failure_reason: string;
  handled: boolean;
  id: string;
  message: SESMessage;
  message_id: string;
  modified_at: string;
};
export type SESMessage = {
  mail: SESMail;
  receipt: SESReceipt;
};
export type SESMail = {
  commonHeaders: SESMailCommonHeaders;
  destination: Array<string>;
  headers: Array<SESMailHeader>;
  headersTruncated: boolean;
  messageId: string;
  source: string;
  timestamp: string;
};
export type SESMailCommonHeaders = Partial<{
  bcc: (Array<string> | null) | Array<Array<string> | null>;
  cc: (Array<string> | null) | Array<Array<string> | null>;
  date: string;
  from: (Array<string> | null) | Array<Array<string> | null>;
  messageId: string;
  replyTo: (Array<string> | null) | Array<Array<string> | null>;
  returnPath: string;
  sender: (Array<string> | null) | Array<Array<string> | null>;
  subject: string;
  to: (Array<string> | null) | Array<Array<string> | null>;
}>;
export type SESMailHeader = {
  name: string;
  value: string;
};
export type SESReceipt = Partial<{
  action:
    | (
        | SESReceiptS3Action
        | SESReceiptSnsAction
        | SESReceiptBounceAction
        | SESReceiptLambdaAction
        | SESReceiptStopAction
        | SESReceiptWorkMailAction
        | {}
      )
    | Array<
        | SESReceiptS3Action
        | SESReceiptSnsAction
        | SESReceiptBounceAction
        | SESReceiptLambdaAction
        | SESReceiptStopAction
        | SESReceiptWorkMailAction
        | {}
      >;
  dkimVerdict: SESReceiptStatus;
  /**
   * @enum none, quarantine, reject
   */
  dmarcPolicy: "none" | "quarantine" | "reject";
  dmarcVerdict: SESReceiptStatus;
  processingTimeMillis: number;
  recipients: Array<string>;
  spamVerdict: SESReceiptStatus;
  spfVerdict: SESReceiptStatus;
  timestamp: string;
  virusVerdict: SESReceiptStatus;
}>;
export type SESReceiptS3Action = Partial<{
  bucketName: string;
  objectKey: string;
  topicArn: (string | null) | Array<string | null>;
  type: string;
}>;
export type SESReceiptSnsAction = Partial<{
  topicArn: string;
  type: string;
}>;
export type SESReceiptBounceAction = Partial<{
  message: string;
  sender: string;
  smtpReplyCode: string;
  statusCode: string;
  topicArn: (string | null) | Array<string | null>;
  type: string;
}>;
export type SESReceiptLambdaAction = Partial<{
  functionArn: string;
  invocationType: string;
  topicArn: (string | null) | Array<string | null>;
  type: string;
}>;
export type SESReceiptStopAction = Partial<{
  topicArn: (string | null) | Array<string | null>;
  type: string;
}>;
export type SESReceiptWorkMailAction = Partial<{
  organizationArn: string;
  topicArn: (string | null) | Array<string | null>;
  type: string;
}>;
export type SESReceiptStatus = {
  /**
   * @enum PASS, FAIL, GRAY
   */
  status: "PASS" | "FAIL" | "GRAY";
};
export type FailedEmailQueueItemList = Array<FailedEmailQueueItem>;
export type FileCategory = {
  defaultClassification: FileClassification;
  id: string;
  name: string;
  path: string;
  pathOptions: Array<FilePathOption>;
};
export type FilePathOption = {
  name: string;
  parameters: {};
  type: FilePathOptionType;
};
export type FilePathOptionType =
  /**
   * @enum string, year, month
   */
  "string" | "year" | "month";
export type FileCategoryList = Array<FileCategory>;
export type FileVersionList = Array<FileVersion>;
export type FileVersion = {
  id: string;
};
export type InternalCreateCase = {
  accountId: (string | null) | Array<string | null>;
  caseType: CaseType;
  crmContactId?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  /**
   * @maxLength 9000
   */
  description: string;
  origin: CaseOrigin;
  priority: CasePriority;
  requestType?:
    | /**
     * @default null
     */
    ((CaseRequestType | null) | Array<CaseRequestType | null>)
    | undefined;
  requestTypeInt?:
    | /**
     * @default null
     */
    ((number | null) | Array<number | null>)
    | undefined;
  requestUserCrmContactId?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  requesterEmail?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  severity: CaseSeverity;
  /**
   * @maxLength 200
   */
  title: string;
};
export type NewApiCredential = {
  clientId: string;
  clientSecret: string;
  name: string;
  portalRole: PortalRole;
};
export type NodeDataPrint = {
  charts: Array<DashboardWidget>;
  node: NodeInfo;
  view: DataPrintView;
  views: {};
};
export type NodeInfo = {
  defaultTable: string;
  environmentId: number;
  inspectorAlias: string;
  inspectorId: number;
  label: string;
  name: string;
  nodeId: number;
};
export type NodeList = Array<NodeListItem>;
export type NodeListItem = {
  id: number;
  inspectorAlias: string;
  label: string;
  name: string;
};
export type OrgLinkList = Array<OrgLink>;
export type OrgLink = {
  icon_url: (string | null) | Array<string | null>;
  name: string;
  url: string;
};
export type PutAlertEmailParseRule = {
  alert_time_format?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  alert_time_regex?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  alert_type: AlertType;
  alert_url_has_alert_id: boolean;
  alert_url_regex: string;
  enabled: boolean;
  source_email: string;
  source_name: string;
};
export type SystemDataPrint = {
  system: DataPrintSystem;
  view: DataPrintView;
  views: {};
};
export type SystemList = Array<SystemListItem>;
export type SystemListItem = {
  id: number;
  inspectorAlias: string;
  name: string;
};
export type TreeNode = {
  children: (Array<TreeNode> | null) | Array<Array<TreeNode> | null>;
  className?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  label: string;
  value: (string | null) | Array<string | null>;
};
export type TreeNodeList = Array<TreeNode>;
export type UpdateAlert = Partial<{
  /**
   * @default null
   */
  ack_by: (string | null) | Array<string | null>;
  /**
   * @default null
   */
  ack_comment: (string | null) | Array<string | null>;
  /**
   * @default null
   */
  ack_date: (string | null) | Array<string | null>;
  /**
   * @default null
   */
  clear_date: (string | null) | Array<string | null>;
  /**
   * @default null
   */
  detail: (string | null) | Array<string | null>;
  /**
   * @default null
   */
  external_alert_status: (string | null) | Array<string | null>;
  /**
   * @default null
   */
  severity: (string | null) | Array<string | null>;
  /**
   * @default null
   */
  status: (AlertStatus | null) | Array<AlertStatus | null>;
  /**
   * @default null
   */
  threshold_date: (string | null) | Array<string | null>;
  /**
   * @default null
   */
  title: (string | null) | Array<string | null>;
  /**
   * @default null
   */
  trigger_date: (string | null) | Array<string | null>;
}>;
export type User = {
  accountCrmId: string;
  accountId: string;
  accountIsCustomer: boolean;
  accountIsDemo: boolean;
  accountIsParent: boolean;
  accountIsReferrer: boolean;
  accountIsRoot: boolean;
  accountIsServiceProvider: boolean;
  accountName: string;
  accountState: number;
  crmContactId?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  do_not_auto_respond: boolean;
  email: (string | null) | Array<string | null>;
  firstName: string;
  gimp: boolean;
  id: string;
  lastName: string;
  mobilePhone: (string | null) | Array<string | null>;
  name: string;
  officePhone: (string | null) | Array<string | null>;
  permissions: Array<string>;
  portalRole: (PortalRole | null) | Array<PortalRole | null>;
  portalUrl: (string | null) | Array<string | null>;
  serviceProviderId: (string | null) | Array<string | null>;
  serviceProviderName: (string | null) | Array<string | null>;
};

const CRMItemType = z.enum(["alert", "case"]);
const AttachQueueToItemBody: z.ZodType<AttachQueueToItemBody> = z
  .object({
    item_id: z.string().uuid(),
    item_type: CRMItemType,
    queue_stub: z.string(),
  })
  .passthrough();
const ValidationErrorModel = z
  .object({
    ctx: z
      .union([z.object({}).partial().passthrough(), z.null()])
      .default(null),
    loc: z.union([z.array(z.string()), z.null()]).default(null),
    msg: z.union([z.string(), z.null()]).default(null),
    type_: z.union([z.string(), z.null()]).default(null),
  })
  .partial()
  .passthrough();
const AuthUserInfoRequest = z
  .object({
    authProvider: z.union([z.string(), z.null()]).default(null),
    authProviderUserId: z.union([z.string(), z.null()]).default(null),
    email: z.union([z.string(), z.null()]).default(null),
    wdUserId: z.union([z.string(), z.null()]).default(null),
  })
  .partial()
  .passthrough();
const UserInfoAccount: z.ZodType<UserInfoAccount> = z
  .object({
    id: z.string().uuid(),
    is_customer: z.boolean(),
    is_parent: z.boolean(),
    is_referrer: z.boolean(),
    is_root: z.boolean(),
    is_service_provider: z.boolean(),
    name: z.string(),
    role: z.string(),
  })
  .passthrough();
const UserInfoServiceProvider: z.ZodType<UserInfoServiceProvider> = z
  .object({
    account_id: z.union([z.string(), z.null()]),
    account_name: z.union([z.string(), z.null()]),
  })
  .passthrough();
const LegacyMetadataUserInfo: z.ZodType<LegacyMetadataUserInfo> = z
  .object({
    account: UserInfoAccount,
    contact_id: z.string().uuid(),
    gimp: z.boolean(),
    id: z.string().uuid(),
    portalUrl: z.string(),
    role: z.string(),
    service_provider: UserInfoServiceProvider,
  })
  .passthrough();
const LegacyMetadataRoot: z.ZodType<LegacyMetadataRoot> = z
  .object({ user_info: LegacyMetadataUserInfo })
  .passthrough();
const AuthUserInfoResponse: z.ZodType<AuthUserInfoResponse> = z
  .object({
    account: UserInfoAccount,
    email: z.union([z.string(), z.null()]),
    email_verified: z.boolean(),
    family_name: z.string(),
    given_name: z.string(),
    name: z.string(),
    portal_url: z.string(),
    service_provider: UserInfoServiceProvider,
    sub: z.string().uuid(),
    updated_at: z.union([z.string(), z.null()]).optional().default(null),
    whitedog_app_metadata: LegacyMetadataRoot,
  })
  .passthrough();
const ClientCredentials = z
  .object({ client_id: z.string().uuid(), client_secret: z.string() })
  .passthrough();
const PortalRole = z.enum([
  "global_super_admin",
  "global_admin",
  "admin",
  "support",
  "sales",
  "demo",
  "user",
]);
const User: z.ZodType<User> = z
  .object({
    accountCrmId: z.string().uuid(),
    accountId: z.string().uuid(),
    accountIsCustomer: z.boolean(),
    accountIsDemo: z.boolean(),
    accountIsParent: z.boolean(),
    accountIsReferrer: z.boolean(),
    accountIsRoot: z.boolean(),
    accountIsServiceProvider: z.boolean(),
    accountName: z.string(),
    accountState: z.number().int(),
    crmContactId: z.union([z.string(), z.null()]).optional().default(null),
    do_not_auto_respond: z.boolean(),
    email: z.union([z.string(), z.null()]),
    firstName: z.string(),
    gimp: z.boolean(),
    id: z.string().uuid(),
    lastName: z.string(),
    mobilePhone: z.union([z.string(), z.null()]),
    name: z.string(),
    officePhone: z.union([z.string(), z.null()]),
    permissions: z.array(z.string()),
    portalRole: z.union([PortalRole, z.null()]),
    portalUrl: z.union([z.string(), z.null()]),
    serviceProviderId: z.union([z.string(), z.null()]),
    serviceProviderName: z.union([z.string(), z.null()]),
  })
  .passthrough();
const CanImpersonateRequest = z
  .object({
    impersonate_user_id: z.string().uuid(),
    real_user_id: z.string().uuid(),
  })
  .passthrough();
const CanImpersonateResponse = z.boolean();
const CaseType = z.enum([
  "question",
  "problem",
  "request",
  "incident",
  "quarantine",
]);
const CaseOrigin = z.enum([
  "phone",
  "email",
  "portal",
  "social",
  "form",
  "alert",
  "api",
]);
const CasePriority = z.enum(["urgent", "high", "medium", "low"]);
const CaseRequestType = z.enum(["support", "quote"]);
const CaseSeverity = z.enum(["moderate", "major", "critical", "minor"]);
const InternalCreateCase: z.ZodType<InternalCreateCase> = z
  .object({
    accountId: z.union([z.string(), z.null()]),
    caseType: CaseType,
    crmContactId: z.union([z.string(), z.null()]).optional().default(null),
    description: z.string().max(9000),
    origin: CaseOrigin,
    priority: CasePriority,
    requestType: z.union([CaseRequestType, z.null()]).optional().default(null),
    requestTypeInt: z.union([z.number(), z.null()]).optional().default(null),
    requestUserCrmContactId: z
      .union([z.string(), z.null()])
      .optional()
      .default(null),
    requesterEmail: z.union([z.string(), z.null()]).optional().default(null),
    severity: CaseSeverity,
    title: z.string().max(200),
  })
  .passthrough();
const CaseState = z.enum(["active", "resolved", "cancelled"]);
const CaseStatus = z.enum([
  "in_progress",
  "on_hold",
  "waiting_on_customer",
  "waiting_on_partner",
  "solved",
  "cancelled",
  "answered",
  "duplicate",
  "quarantine",
  "new",
  "reopen",
  "review_pending",
]);
const Case: z.ZodType<Case> = z
  .object({
    assignedQueueName: z.union([z.string(), z.null()]),
    caseNumber: z.string(),
    caseType: z.union([CaseType, z.null()]),
    createdAt: z.string().datetime({ offset: true }),
    createdById: z.union([z.string(), z.null()]),
    createdByName: z.union([z.string(), z.null()]),
    createdOnBehalfOfId: z.union([z.string(), z.null()]),
    createdOnBehalfOfName: z.union([z.string(), z.null()]),
    description: z.union([z.string(), z.null()]),
    id: z.string().uuid(),
    latestActivityDate: z.union([z.string(), z.null()]),
    modifiedAt: z.string().datetime({ offset: true }),
    origin: z.union([CaseOrigin, z.null()]),
    priority: z.union([CasePriority, z.null()]),
    resolved_date: z.union([z.string(), z.null()]),
    severity: z.union([CaseSeverity, z.null()]),
    state: CaseState,
    status: CaseStatus,
    ticketNumber: z.string(),
    title: z.string(),
  })
  .passthrough();
const EmailPriority = z.enum(["low", "normal", "high"]);
const CreateRelatedWDCEmailActivity: z.ZodType<CreateRelatedWDCEmailActivity> =
  z
    .object({
      bcc_contacts: z.array(z.string().uuid()).optional(),
      cc_contacts: z.array(z.string().uuid()).optional(),
      conv_id: z.union([z.string(), z.null()]).optional().default(null),
      from_contact: z.union([z.string(), z.null()]).optional().default(null),
      html_body: z.string(),
      is_customer_notification: z.boolean().optional().default(false),
      priority: EmailPriority.optional(),
      related_item_id: z.string().uuid(),
      related_item_type: CRMItemType,
      subject: z.string(),
      to_addresses: z.array(z.string()).optional(),
      to_contacts: z.array(z.string().uuid()).optional(),
    })
    .passthrough();
const CRMTypeEnum = z.enum(["alert", "incident", "activity"]);
const CRMWebhookBody: z.ZodType<CRMWebhookBody> = z
  .object({
    object_id: z.string().uuid(),
    object_type: CRMTypeEnum,
    org_id: z.string().uuid(),
    parent_object_id: z.union([z.string(), z.null()]),
    parent_object_type: z.union([CRMTypeEnum, z.null()]),
  })
  .passthrough();
const SendWdcEmailBody = z
  .object({ email_id: z.string().uuid() })
  .passthrough();
const Account: z.ZodType<Account> = z
  .object({
    crmAccountId: z.string().uuid(),
    entitlements: z
      .union([z.array(z.string()), z.null()])
      .optional()
      .default(null),
    id: z.string().uuid(),
    isCustomer: z.boolean().optional().default(true),
    isDemo: z.boolean().optional().default(false),
    isParent: z.boolean().optional().default(false),
    isReferrer: z.boolean().optional().default(false),
    isRoot: z.boolean().optional().default(false),
    isServiceProvider: z.boolean().optional().default(false),
    name: z.string(),
    parentalLineage: z.union([z.string(), z.null()]),
    portalStub: z.union([z.string(), z.null()]),
    portalUrl: z.union([z.string(), z.null()]),
    providerLineage: z.union([z.string(), z.null()]),
    referralLineage: z.union([z.string(), z.null()]),
    serviceProviderId: z.union([z.string(), z.null()]),
    serviceProviderName: z.union([z.string(), z.null()]),
    serviceProviderStub: z.union([z.string(), z.null()]),
  })
  .passthrough();
const AccountList: z.ZodType<AccountList> = z.array(Account);
const AlertType = z.enum(["security", "operations", "undefined", "test"]);
const AlertEmailParseRule: z.ZodType<AlertEmailParseRule> = z
  .object({
    alert_time_format: z.union([z.string(), z.null()]).optional().default(null),
    alert_time_regex: z.union([z.string(), z.null()]).optional().default(null),
    alert_type: AlertType,
    alert_url_has_alert_id: z.boolean(),
    alert_url_regex: z.string(),
    enabled: z.boolean(),
    id: z.string().uuid(),
    source_email: z.string(),
    source_name: z.string(),
  })
  .passthrough();
const AlertEmailParseRuleList: z.ZodType<AlertEmailParseRuleList> =
  z.array(AlertEmailParseRule);
const CreateAlertEmailParseRule: z.ZodType<CreateAlertEmailParseRule> = z
  .object({
    alert_time_format: z.union([z.string(), z.null()]).optional().default(null),
    alert_time_regex: z.union([z.string(), z.null()]).optional().default(null),
    alert_type: AlertType,
    alert_url_has_alert_id: z.boolean(),
    alert_url_regex: z.string(),
    enabled: z.boolean(),
    source_email: z.string(),
    source_name: z.string(),
  })
  .passthrough();
const PutAlertEmailParseRule: z.ZodType<PutAlertEmailParseRule> = z
  .object({
    alert_time_format: z.union([z.string(), z.null()]).optional().default(null),
    alert_time_regex: z.union([z.string(), z.null()]).optional().default(null),
    alert_type: AlertType,
    alert_url_has_alert_id: z.boolean(),
    alert_url_regex: z.string(),
    enabled: z.boolean(),
    source_email: z.string(),
    source_name: z.string(),
  })
  .passthrough();
const DebugEmailBody = z.object({ message_id: z.string() }).passthrough();
const EmailTemplateListItem: z.ZodType<EmailTemplateListItem> = z
  .object({
    created_on: z.string().datetime({ offset: true }),
    name: z.string(),
  })
  .passthrough();
const EmailTemplateList: z.ZodType<EmailTemplateList> = z.array(
  EmailTemplateListItem
);
const UpsertEmailTemplate = z
  .object({
    html: z.string(),
    name: z.string(),
    subject: z.string(),
    text: z.string(),
  })
  .passthrough();
const EmailTemplate = z
  .object({
    html: z.string(),
    name: z.string(),
    subject: z.string(),
    text: z.string(),
  })
  .passthrough();
const limit = z.union([z.number(), z.null()]).optional().default(null);
const sort = z
  .union([z.array(z.string()), z.null()])
  .optional()
  .default(null);
const filter = z.union([z.string(), z.null()]).optional().default(null);
const SESMailCommonHeaders: z.ZodType<SESMailCommonHeaders> = z
  .object({
    bcc: z.union([z.array(z.string()), z.null()]),
    cc: z.union([z.array(z.string()), z.null()]),
    date: z.string(),
    from: z.union([z.array(z.string()), z.null()]),
    messageId: z.string(),
    replyTo: z.union([z.array(z.string()), z.null()]),
    returnPath: z.string(),
    sender: z.union([z.array(z.string()), z.null()]),
    subject: z.string(),
    to: z.union([z.array(z.string()), z.null()]),
  })
  .partial()
  .passthrough();
const SESMailHeader: z.ZodType<SESMailHeader> = z
  .object({ name: z.string(), value: z.string() })
  .passthrough();
const SESMail: z.ZodType<SESMail> = z
  .object({
    commonHeaders: SESMailCommonHeaders,
    destination: z.array(z.string()),
    headers: z.array(SESMailHeader),
    headersTruncated: z.boolean(),
    messageId: z.string(),
    source: z.string(),
    timestamp: z.string(),
  })
  .passthrough();
const SESReceiptS3Action: z.ZodType<SESReceiptS3Action> = z
  .object({
    bucketName: z.string(),
    objectKey: z.string(),
    topicArn: z.union([z.string(), z.null()]),
    type: z.string(),
  })
  .partial()
  .passthrough();
const SESReceiptSnsAction: z.ZodType<SESReceiptSnsAction> = z
  .object({ topicArn: z.string(), type: z.string() })
  .partial()
  .passthrough();
const SESReceiptBounceAction: z.ZodType<SESReceiptBounceAction> = z
  .object({
    message: z.string(),
    sender: z.string(),
    smtpReplyCode: z.string(),
    statusCode: z.string(),
    topicArn: z.union([z.string(), z.null()]),
    type: z.string(),
  })
  .partial()
  .passthrough();
const SESReceiptLambdaAction: z.ZodType<SESReceiptLambdaAction> = z
  .object({
    functionArn: z.string(),
    invocationType: z.string(),
    topicArn: z.union([z.string(), z.null()]),
    type: z.string(),
  })
  .partial()
  .passthrough();
const SESReceiptStopAction: z.ZodType<SESReceiptStopAction> = z
  .object({ topicArn: z.union([z.string(), z.null()]), type: z.string() })
  .partial()
  .passthrough();
const SESReceiptWorkMailAction: z.ZodType<SESReceiptWorkMailAction> = z
  .object({
    organizationArn: z.string(),
    topicArn: z.union([z.string(), z.null()]),
    type: z.string(),
  })
  .partial()
  .passthrough();
const SESReceiptStatus: z.ZodType<SESReceiptStatus> = z
  .object({ status: z.enum(["PASS", "FAIL", "GRAY"]) })
  .passthrough();
const SESReceipt: z.ZodType<SESReceipt> = z
  .object({
    action: z.union([
      SESReceiptS3Action,
      SESReceiptSnsAction,
      SESReceiptBounceAction,
      SESReceiptLambdaAction,
      SESReceiptStopAction,
      SESReceiptWorkMailAction,
      z.object({}).partial().passthrough(),
    ]),
    dkimVerdict: SESReceiptStatus,
    dmarcPolicy: z.enum(["none", "quarantine", "reject"]),
    dmarcVerdict: SESReceiptStatus,
    processingTimeMillis: z.number().int(),
    recipients: z.array(z.string()),
    spamVerdict: SESReceiptStatus,
    spfVerdict: SESReceiptStatus,
    timestamp: z.string(),
    virusVerdict: SESReceiptStatus,
  })
  .partial()
  .passthrough();
const SESMessage: z.ZodType<SESMessage> = z
  .object({ mail: SESMail, receipt: SESReceipt })
  .passthrough();
const FailedEmailQueueItem: z.ZodType<FailedEmailQueueItem> = z
  .object({
    created_at: z.string().datetime({ offset: true }),
    failure_reason: z.string(),
    handled: z.boolean(),
    id: z.string().uuid(),
    message: SESMessage,
    message_id: z.string(),
    modified_at: z.string().datetime({ offset: true }),
  })
  .passthrough();
const FailedEmailQueueItemList: z.ZodType<FailedEmailQueueItemList> =
  z.array(FailedEmailQueueItem);
const BulkRetryRequest = z
  .object({ address: z.string(), reason: z.string() })
  .passthrough();
const Reason = z.object({ reason: z.string() }).passthrough();
const AlertState = z.enum(["active", "inactive", "in_progress", "closed"]);
const AlertStatus = z.enum([
  "new",
  "on_hold",
  "clear",
  "canceled",
  "in_progress_case_created",
]);
const Alert: z.ZodType<Alert> = z
  .object({
    ack_by: z.union([z.string(), z.null()]),
    ack_comment: z.union([z.string(), z.null()]),
    ack_date: z.union([z.string(), z.null()]),
    alert_source: z.union([z.string(), z.null()]),
    alert_time: z.union([z.string(), z.null()]),
    alert_type: AlertType,
    clear_date: z.union([z.string(), z.null()]),
    created_date: z.string().datetime({ offset: true }),
    detail: z.union([z.string(), z.null()]),
    entity: z.union([z.string(), z.null()]),
    entity_description: z.union([z.string(), z.null()]),
    entity_ip_address: z.union([z.string(), z.null()]),
    entity_model: z.union([z.string(), z.null()]),
    entity_type: z.union([z.string(), z.null()]),
    external_alert_id: z.union([z.string(), z.null()]),
    external_alert_status: z.union([z.string(), z.null()]),
    external_alert_url: z.union([z.string(), z.null()]),
    id: z.string().uuid(),
    name: z.string(),
    related_case_id: z.union([z.string(), z.null()]),
    severity: z.union([z.string(), z.null()]),
    state: AlertState,
    status: AlertStatus,
    threshold_date: z.union([z.string(), z.null()]),
    trigger_date: z.union([z.string(), z.null()]),
  })
  .passthrough();
const AlertList: z.ZodType<AlertList> = z.array(Alert);
const CreateAlert: z.ZodType<CreateAlert> = z
  .object({
    alert_source: z.string(),
    alert_time: z.string().datetime({ offset: true }),
    alert_type: AlertType,
    detail: z.string(),
    entity: z.union([z.string(), z.null()]).optional().default(null),
    entity_description: z
      .union([z.string(), z.null()])
      .optional()
      .default(null),
    entity_ip_address: z.union([z.string(), z.null()]).optional().default(null),
    entity_model: z.union([z.string(), z.null()]).optional().default(null),
    entity_type: z.union([z.string(), z.null()]).optional().default(null),
    external_alert_id: z.union([z.string(), z.null()]).optional().default(null),
    external_alert_status: z
      .union([z.string(), z.null()])
      .optional()
      .default(null),
    external_alert_url: z
      .union([z.string(), z.null()])
      .optional()
      .default(null),
    severity: z.union([z.string(), z.null()]).optional().default(null),
    threshold_date: z.union([z.string(), z.null()]).optional().default(null),
    title: z.string().max(250),
    trigger_date: z.union([z.string(), z.null()]).optional().default(null),
  })
  .passthrough();
const UpdateAlert: z.ZodType<UpdateAlert> = z
  .object({
    ack_by: z.union([z.string(), z.null()]).default(null),
    ack_comment: z.union([z.string(), z.null()]).default(null),
    ack_date: z.union([z.string(), z.null()]).default(null),
    clear_date: z.union([z.string(), z.null()]).default(null),
    detail: z.union([z.string(), z.null()]).default(null),
    external_alert_status: z.union([z.string(), z.null()]).default(null),
    severity: z.union([z.string(), z.null()]).default(null),
    status: z.union([AlertStatus, z.null()]).default(null),
    threshold_date: z.union([z.string(), z.null()]).default(null),
    title: z.union([z.string(), z.null()]).default(null),
    trigger_date: z.union([z.string(), z.null()]).default(null),
  })
  .partial()
  .passthrough();
const AlertActivity: z.ZodType<AlertActivity> = z
  .object({
    activityType: z.string(),
    body: z.string(),
    createdDate: z.string().datetime({ offset: true }),
    id: z.string().uuid(),
    sender: z.union([z.string(), z.null()]),
    subject: z.string(),
  })
  .passthrough();
const AlertActivityList: z.ZodType<AlertActivityList> = z.array(AlertActivity);
const ApiCredential: z.ZodType<ApiCredential> = z
  .object({
    clientId: z.string().uuid(),
    name: z.string(),
    portalRole: PortalRole,
  })
  .passthrough();
const ApiCredentialList: z.ZodType<ApiCredentialList> = z.array(ApiCredential);
const CreateApiCredential = z.object({ name: z.string() }).passthrough();
const NewApiCredential: z.ZodType<NewApiCredential> = z
  .object({
    clientId: z.string().uuid(),
    clientSecret: z.string(),
    name: z.string(),
    portalRole: PortalRole,
  })
  .passthrough();
const PatchApiCredential = z
  .object({ name: z.union([z.string(), z.null()]) })
  .passthrough();
const BrandResponse = z
  .object({
    appName: z.string(),
    apps: z.array(z.string()),
    brandLogo: z.string(),
    brandName: z.string(),
    brandPath: z.string(),
    navLogo: z.string(),
    themeName: z.string(),
  })
  .passthrough();
const BrandPatch = z
  .object({
    appName: z.union([z.string(), z.null()]).default(null),
    apps: z.union([z.array(z.string()), z.null()]).default(null),
    brandLogoRaw: z.union([z.string(), z.null()]).default(null),
    brandName: z.union([z.string(), z.null()]).default(null),
    navLogoRaw: z.union([z.string(), z.null()]).default(null),
    themeName: z.union([z.string(), z.null()]).default(null),
  })
  .partial()
  .passthrough();
const BrandPut = z
  .object({
    appName: z.string(),
    apps: z
      .union([z.array(z.string()), z.null()])
      .optional()
      .default(null),
    brandLogoRaw: z.union([z.string(), z.null()]).optional().default(null),
    brandName: z.string(),
    navLogoRaw: z.union([z.string(), z.null()]).optional().default(null),
    themeName: z.string().optional().default("whitedog"),
  })
  .passthrough();
const CaseList: z.ZodType<CaseList> = z.array(Case);
const CreateCase: z.ZodType<CreateCase> = z
  .object({
    caseType: CaseType,
    description: z.string().max(9000),
    origin: CaseOrigin,
    priority: CasePriority,
    requestType: z.union([CaseRequestType, z.null()]).optional().default(null),
    severity: CaseSeverity,
    title: z.string().max(200),
  })
  .passthrough();
const CaseActivity: z.ZodType<CaseActivity> = z
  .object({
    activityType: z.string(),
    body: z.string(),
    createdDate: z.string().datetime({ offset: true }),
    id: z.string().uuid(),
    sender: z.union([z.string(), z.null()]),
    subject: z.string(),
  })
  .passthrough();
const CaseActivityList: z.ZodType<CaseActivityList> = z.array(CaseActivity);
const File: z.ZodType<File> = z
  .object({
    createdTime: z.string().datetime({ offset: true }),
    fileMetadata: z
      .union([z.object({}).partial().passthrough(), z.null()])
      .optional()
      .default(null),
    id: z.string().uuid(),
    mimeType: z.union([z.string(), z.null()]).optional().default(null),
    modifiedTime: z.string().datetime({ offset: true }),
    name: z.string(),
    path: z.string(),
    pathMetadata: z.object({}).partial().passthrough(),
    size: z.union([z.number(), z.null()]).optional().default(null),
    totalArchiveSize: z.number().int(),
    totalLiveSize: z.number().int(),
    type: z.string().optional().default("file"),
  })
  .passthrough();
const Folder: z.ZodType<Folder> = z
  .object({
    modifiedAt: z.string().datetime({ offset: true }),
    name: z.string(),
    size: z.number().int(),
    totalArchiveSize: z.number().int(),
    totalLiveSize: z.number().int(),
    type: z.string().optional().default("folder"),
  })
  .passthrough();
const DirectoryListing: z.ZodType<DirectoryListing> = z.array(
  z.union([File, Folder])
);
const FileClassification = z.union([
  z.literal(0),
  z.literal(10),
  z.literal(20),
  z.literal(30),
  z.literal(40),
]);
const CreateFile: z.ZodType<CreateFile> = z
  .object({
    category: z.string().uuid(),
    classification: z
      .union([FileClassification, z.null()])
      .optional()
      .default(null),
    fileMetadata: z
      .union([z.object({}).partial().passthrough(), z.null()])
      .optional()
      .default(null),
    name: z.string(),
    pathMetadata: z.object({}).partial().passthrough(),
    upsert: z.boolean().optional().default(false),
  })
  .passthrough();
const FilePathOptionType = z.enum(["string", "year", "month"]);
const FilePathOption: z.ZodType<FilePathOption> = z
  .object({
    name: z.string(),
    parameters: z.object({}).partial().passthrough(),
    type: FilePathOptionType,
  })
  .passthrough();
const FileCategory: z.ZodType<FileCategory> = z
  .object({
    defaultClassification: FileClassification,
    id: z.string().uuid(),
    name: z.string(),
    path: z.string(),
    pathOptions: z.array(FilePathOption),
  })
  .passthrough();
const FileCategoryList: z.ZodType<FileCategoryList> = z.array(FileCategory);
const FileStats = z
  .object({
    archiveFileCount: z.number().int(),
    liveFileCount: z.number().int(),
    totalFileCount: z.number().int(),
    totalSize: z.number().int(),
    totalUsed: z.number().int(),
    usedArchive: z.number().int(),
    usedLive: z.number().int(),
  })
  .passthrough();
const FileUrl = z.object({ url: z.string() }).passthrough();
const FileVersion: z.ZodType<FileVersion> = z
  .object({ id: z.string().uuid() })
  .passthrough();
const FileVersionList: z.ZodType<FileVersionList> = z.array(FileVersion);
const FileVersionUploadResponse = z
  .object({
    id: z.string().uuid(),
    virusScanJobId: z.union([z.string(), z.null()]),
  })
  .passthrough();
const JobStatus = z
  .object({
    id: z.string(),
    result: z.union([z.unknown(), z.null()]),
    status: z.string(),
  })
  .passthrough();
const NodeListItem: z.ZodType<NodeListItem> = z
  .object({
    id: z.number().int(),
    inspectorAlias: z.string(),
    label: z.string(),
    name: z.string(),
  })
  .passthrough();
const NodeList: z.ZodType<NodeList> = z.array(NodeListItem);
const FrontendColor = z.enum([
  "critical",
  "high",
  "medium",
  "low",
  "success",
  "warning",
  "danger",
  "info",
  "nav",
  "light",
  "dark",
  "primary",
  "secondary",
]);
const PieChartWidgetDataEntry: z.ZodType<PieChartWidgetDataEntry> = z
  .object({
    color: z.union([FrontendColor, z.string(), z.null()]),
    label: z.string(),
    value: z.number(),
  })
  .passthrough();
const PieChartWidgetPie: z.ZodType<PieChartWidgetPie> = z
  .object({ data: z.array(PieChartWidgetDataEntry) })
  .passthrough();
const PieChartWidgetConfiguration: z.ZodType<PieChartWidgetConfiguration> = z
  .object({
    pies: z.array(PieChartWidgetPie),
    showLegend: z.union([z.boolean(), z.null()]),
    showTooltip: z.union([z.boolean(), z.null()]),
  })
  .passthrough();
const BarChartWidgetBar: z.ZodType<BarChartWidgetBar> = z
  .object({
    colorKey: z.union([z.string(), z.null()]),
    dataKey: z.string(),
    label: z.union([z.string(), z.null()]),
  })
  .passthrough();
const BarChartWidgetConfigration: z.ZodType<BarChartWidgetConfigration> = z
  .object({
    bars: z.array(BarChartWidgetBar),
    showGrid: z.union([z.boolean(), z.null()]),
    showLegend: z.union([z.boolean(), z.null()]),
    showTooltip: z.union([z.boolean(), z.null()]),
    useUniformBarColor: z.union([z.boolean(), z.null()]),
    xAxisLabelKey: z.union([z.string(), z.null()]),
    yAxisLabelKey: z.union([z.string(), z.null()]),
  })
  .passthrough();
const DataColumnType = z.enum([
  "string",
  "humandatetime",
  "datetime",
  "boolean",
  "number",
  "currency",
  "percentage",
  "mood",
]);
const DataColumn: z.ZodType<DataColumn> = z
  .object({
    default: z.union([z.boolean(), z.null()]),
    displayName: z.string(),
    enableFilter: z.union([z.boolean(), z.null()]),
    enableSort: z.union([z.boolean(), z.null()]),
    name: z.string(),
    sortInverted: z.union([z.boolean(), z.null()]),
    type: DataColumnType,
    width: z.union([z.number(), z.null()]),
  })
  .passthrough();
const TableWidgetConfiguration: z.ZodType<TableWidgetConfiguration> = z
  .object({ columns: z.array(DataColumn) })
  .passthrough();
const WidgetType = z.enum([
  "pie",
  "bar",
  "table",
  "scoreboard",
  "simple_table",
]);
const DashboardWidget: z.ZodType<DashboardWidget> = z
  .object({
    configuration: z.union([
      PieChartWidgetConfiguration,
      BarChartWidgetConfigration,
      TableWidgetConfiguration,
    ]),
    data: z.union([z.array(z.object({}).partial().passthrough()), z.null()]),
    label: z.string(),
    type: WidgetType,
  })
  .passthrough();
const NodeInfo: z.ZodType<NodeInfo> = z
  .object({
    defaultTable: z.string(),
    environmentId: z.number().int(),
    inspectorAlias: z.string(),
    inspectorId: z.number().int(),
    label: z.string(),
    name: z.string(),
    nodeId: z.number().int(),
  })
  .passthrough();
const SortField: z.ZodType<SortField> = z
  .object({ desc: z.boolean(), id: z.string() })
  .passthrough();
const InitialColumn: z.ZodType<InitialColumn> = z
  .object({ name: z.string(), show: z.boolean() })
  .passthrough();
const ViewDefinitionViewItem: z.ZodType<ViewDefinitionViewItem> = z
  .object({
    Columns: z.union([z.array(z.string()), z.null()]),
    DefaultSorted: z.union([z.array(SortField), z.null()]),
    InitialColumns: z.union([z.array(InitialColumn), z.null()]),
    Label: z.string(),
    Path: z.union([z.string(), z.null()]),
    Rows: z.union([z.array(z.array(z.string())), z.null()]),
    Type: z.string(),
  })
  .passthrough();
const ViewDefinitionView: z.ZodType<ViewDefinitionView> = z
  .object({
    Items: z.array(ViewDefinitionViewItem),
    Title: z.string(),
    ViewType: z.union([z.string(), z.null()]),
  })
  .passthrough();
const ViewDefinition: z.ZodType<ViewDefinition> = z
  .object({ Views: z.array(ViewDefinitionView) })
  .passthrough();
const DataPrintView: z.ZodType<DataPrintView> = z
  .object({ ViewDefinition: ViewDefinition })
  .passthrough();
const NodeDataPrint: z.ZodType<NodeDataPrint> = z
  .object({
    charts: z.array(DashboardWidget),
    node: NodeInfo,
    view: DataPrintView,
    views: z.record(
      z.array(z.union([z.array(z.array(z.unknown())), z.null()]))
    ),
  })
  .passthrough();
const OrgLink: z.ZodType<OrgLink> = z
  .object({
    icon_url: z.union([z.string(), z.null()]),
    name: z.string(),
    url: z.string(),
  })
  .passthrough();
const OrgLinkList: z.ZodType<OrgLinkList> = z.array(OrgLink);
const SystemListItem: z.ZodType<SystemListItem> = z
  .object({
    id: z.number().int(),
    inspectorAlias: z.string(),
    name: z.string(),
  })
  .passthrough();
const SystemList: z.ZodType<SystemList> = z.array(SystemListItem);
const DataPrintSystemInspector: z.ZodType<DataPrintSystemInspector> = z
  .object({ Alias: z.string(), ID: z.number().int() })
  .passthrough();
const DataPrintSystemLaunchpoint: z.ZodType<DataPrintSystemLaunchpoint> = z
  .object({ NextScheduledFor: z.string() })
  .passthrough();
const DataPrintSystem: z.ZodType<DataPrintSystem> = z
  .object({
    Inspector: DataPrintSystemInspector,
    Launchpoint: DataPrintSystemLaunchpoint,
  })
  .passthrough();
const SystemDataPrint: z.ZodType<SystemDataPrint> = z
  .object({
    system: DataPrintSystem,
    view: DataPrintView,
    views: z.record(
      z.array(z.union([z.array(z.array(z.unknown())), z.null()]))
    ),
  })
  .passthrough();
const UserResponse = z.object({}).partial().passthrough();
const TreeNode: z.ZodType<TreeNode> = z.lazy(() =>
  z
    .object({
      children: z.union([z.array(TreeNode), z.null()]),
      className: z.union([z.string(), z.null()]).optional().default(null),
      label: z.string(),
      value: z.union([z.string(), z.null()]),
    })
    .passthrough()
);
const TreeNodeList: z.ZodType<TreeNodeList> = z.array(TreeNode);
const TokenResponse = z.object({ token: z.string() }).passthrough();

export const schemas = {
  CRMItemType,
  AttachQueueToItemBody,
  ValidationErrorModel,
  AuthUserInfoRequest,
  UserInfoAccount,
  UserInfoServiceProvider,
  LegacyMetadataUserInfo,
  LegacyMetadataRoot,
  AuthUserInfoResponse,
  ClientCredentials,
  PortalRole,
  User,
  CanImpersonateRequest,
  CanImpersonateResponse,
  CaseType,
  CaseOrigin,
  CasePriority,
  CaseRequestType,
  CaseSeverity,
  InternalCreateCase,
  CaseState,
  CaseStatus,
  Case,
  EmailPriority,
  CreateRelatedWDCEmailActivity,
  CRMTypeEnum,
  CRMWebhookBody,
  SendWdcEmailBody,
  Account,
  AccountList,
  AlertType,
  AlertEmailParseRule,
  AlertEmailParseRuleList,
  CreateAlertEmailParseRule,
  PutAlertEmailParseRule,
  DebugEmailBody,
  EmailTemplateListItem,
  EmailTemplateList,
  UpsertEmailTemplate,
  EmailTemplate,
  limit,
  sort,
  filter,
  SESMailCommonHeaders,
  SESMailHeader,
  SESMail,
  SESReceiptS3Action,
  SESReceiptSnsAction,
  SESReceiptBounceAction,
  SESReceiptLambdaAction,
  SESReceiptStopAction,
  SESReceiptWorkMailAction,
  SESReceiptStatus,
  SESReceipt,
  SESMessage,
  FailedEmailQueueItem,
  FailedEmailQueueItemList,
  BulkRetryRequest,
  Reason,
  AlertState,
  AlertStatus,
  Alert,
  AlertList,
  CreateAlert,
  UpdateAlert,
  AlertActivity,
  AlertActivityList,
  ApiCredential,
  ApiCredentialList,
  CreateApiCredential,
  NewApiCredential,
  PatchApiCredential,
  BrandResponse,
  BrandPatch,
  BrandPut,
  CaseList,
  CreateCase,
  CaseActivity,
  CaseActivityList,
  File,
  Folder,
  DirectoryListing,
  FileClassification,
  CreateFile,
  FilePathOptionType,
  FilePathOption,
  FileCategory,
  FileCategoryList,
  FileStats,
  FileUrl,
  FileVersion,
  FileVersionList,
  FileVersionUploadResponse,
  JobStatus,
  NodeListItem,
  NodeList,
  FrontendColor,
  PieChartWidgetDataEntry,
  PieChartWidgetPie,
  PieChartWidgetConfiguration,
  BarChartWidgetBar,
  BarChartWidgetConfigration,
  DataColumnType,
  DataColumn,
  TableWidgetConfiguration,
  WidgetType,
  DashboardWidget,
  NodeInfo,
  SortField,
  InitialColumn,
  ViewDefinitionViewItem,
  ViewDefinitionView,
  ViewDefinition,
  DataPrintView,
  NodeDataPrint,
  OrgLink,
  OrgLinkList,
  SystemListItem,
  SystemList,
  DataPrintSystemInspector,
  DataPrintSystemLaunchpoint,
  DataPrintSystem,
  SystemDataPrint,
  UserResponse,
  TreeNode,
  TreeNodeList,
  TokenResponse,
};

export const RestrictedEndpoints = makeApi([
  {
    method: "get",
    path: "/rapi/v1/account_list",
    alias: "rapi_v1_account_list_rapi_v1_account_list_get",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/rapi/v1/attach-queue-to-item",
    alias: "rapi_v1_attach_queue_to_item_rapi_v1_attach_queue_to_item_post",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AttachQueueToItemBody,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/rapi/v1/auth-user-info",
    alias: "rapi_v1_auth_user_info_rapi_v1_auth_user_info_post",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AuthUserInfoRequest,
      },
    ],
    response: AuthUserInfoResponse,
    errors: [
      {
        status: 401,
        description: `Unauthorized`,
        schema: z.void(),
      },
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/rapi/v1/auth/client_credentials",
    alias:
      "rapi_v1_auth_client_credentials_rapi_v1_auth_client_credentials_post",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: ClientCredentials,
      },
    ],
    response: User,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/rapi/v1/auth0_token",
    alias: "rapi_v1_auth0_token_rapi_v1_auth0_token_get",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/rapi/v1/can-user-impersonate-user",
    alias:
      "rapi_v1_can_user_impersonate_user_rapi_v1_can_user_impersonate_user_post",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CanImpersonateRequest,
      },
    ],
    response: z.boolean(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/rapi/v1/create-case",
    alias: "rapi_v1_internal_create_case_rapi_v1_create_case_post",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: InternalCreateCase,
      },
    ],
    response: Case,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/rapi/v1/create-wdc_email-to-send",
    alias:
      "rapi_v1_create_wdc_email_to_send_rapi_v1_create_wdc_email_to_send_post",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateRelatedWDCEmailActivity,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/rapi/v1/crm-inbound-webhook/create",
    alias:
      "rapi_v1_crm_inbound_webhook_create_rapi_v1_crm_inbound_webhook_create_post",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CRMWebhookBody,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/rapi/v1/crm-inbound-webhook/update",
    alias:
      "rapi_v1_crm_inbound_webhook_update_rapi_v1_crm_inbound_webhook_update_post",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CRMWebhookBody,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/rapi/v1/portal_urls",
    alias: "rapi_v1_portal_urls_rapi_v1_portal_urls_get",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/rapi/v1/send-wdc-email-outbound",
    alias:
      "rapi_v1_send_wdc_email_outbound_rapi_v1_send_wdc_email_outbound_post",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ email_id: z.string().uuid() }).passthrough(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/rapi/v1/user-by-email/:email",
    alias: "rapi_v1_user_by_email_rapi_v1_user_by_email__email__get",
    requestFormat: "json",
    parameters: [
      {
        name: "email",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/rapi/v1/webhooks/case/created",
    alias: "rapi_v1_webhooks_case_created_rapi_v1_webhooks_case_created_post",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/rapi/v1/webhooks/case/note/created",
    alias:
      "rapi_v1_webhooks_case_note_created_rapi_v1_webhooks_case_note_created_post",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/rapi/v1/webhooks/case/updated",
    alias: "rapi_v1_webhooks_case_updated_rapi_v1_webhooks_case_updated_post",
    requestFormat: "json",
    response: z.void(),
  },
]);

export const useRestrictedApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_API_URI, RestrictedEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WD-Account": accountContext.accountId,
        "X-WD-Email": userContext.apparentUserEmail,
      },
    },
  });
};

export const AccountEndpoints = makeApi([
  {
    method: "get",
    path: "/v1/accounts",
    alias: "v1_accounts_v1_accounts_get",
    requestFormat: "json",
    response: z.array(Account),
  },
  {
    method: "get",
    path: "/v1/accounts/:account_id",
    alias: "v1_accounts_account_id_v1_accounts__account_id__get",
    requestFormat: "json",
    parameters: [
      {
        name: "account_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Account,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useAccountApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_API_URI, AccountEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WD-Account": accountContext.accountId,
        "X-WD-Email": userContext.apparentUserEmail,
      },
    },
  });
};

export const Alert_Email_Parse_RulesEndpoints = makeApi([
  {
    method: "get",
    path: "/v1/admin/alert_email_parse_rules",
    alias: "list_alert_email_parse_rules",
    requestFormat: "json",
    response: z.array(AlertEmailParseRule),
  },
  {
    method: "post",
    path: "/v1/admin/alert_email_parse_rules",
    alias: "create_alert_email_parse_rule",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateAlertEmailParseRule,
      },
    ],
    response: AlertEmailParseRule,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "delete",
    path: "/v1/admin/alert_email_parse_rules/:id",
    alias: "delete_alert_email_parse_rul_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: AlertEmailParseRule,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/admin/alert_email_parse_rules/:id",
    alias: "get_alert_email_parse_rul_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: AlertEmailParseRule,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "put",
    path: "/v1/admin/alert_email_parse_rules/:id",
    alias: "update_alert_email_parse_rule",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PutAlertEmailParseRule,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: AlertEmailParseRule,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useAlert_Email_Parse_RulesApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(
    import.meta.env.VITE_API_URI,
    Alert_Email_Parse_RulesEndpoints,
    {
      axiosConfig: {
        headers: {
          Authorization: `Bearer ${userContext.accessToken}`,
          "X-WD-Account": accountContext.accountId,
          "X-WD-Email": userContext.apparentUserEmail,
        },
      },
    }
  );
};

export const AdminEndpoints = makeApi([
  {
    method: "post",
    path: "/v1/admin/datasheet_overlay",
    alias: "v1_admin_datasheet_overlay_v1_admin_datasheet_overlay_post",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "post",
    path: "/v1/admin/debug_email",
    alias: "v1_admin_debug_email_v1_admin_debug_email_post",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ message_id: z.string() }).passthrough(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/admin/email_templates",
    alias: "get_email_templates",
    requestFormat: "json",
    response: z.array(EmailTemplateListItem),
  },
  {
    method: "post",
    path: "/v1/admin/email_templates",
    alias: "create_email_template",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpsertEmailTemplate,
      },
    ],
    response: EmailTemplate,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "delete",
    path: "/v1/admin/email_templates/:template_name",
    alias: "delete_email_template",
    requestFormat: "json",
    parameters: [
      {
        name: "template_name",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/admin/email_templates/:template_name",
    alias: "get_email_template",
    requestFormat: "json",
    parameters: [
      {
        name: "template_name",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EmailTemplate,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "put",
    path: "/v1/admin/email_templates/:template_name",
    alias: "update_email_template",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpsertEmailTemplate,
      },
      {
        name: "template_name",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: EmailTemplate,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/admin/failed-email-queue",
    alias: "query_failed_email_queue",
    requestFormat: "json",
    parameters: [
      {
        name: "limit",
        type: "Query",
        schema: limit,
      },
      {
        name: "page_size",
        type: "Query",
        schema: limit,
      },
      {
        name: "sort",
        type: "Query",
        schema: sort,
      },
      {
        name: "filter",
        type: "Query",
        schema: filter,
      },
    ],
    response: z.array(FailedEmailQueueItem),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/v1/admin/failed-email-queue/bulk-retry",
    alias: "bulk_retry_failed_emails",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BulkRetryRequest,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "delete",
    path: "/v1/admin/failed-email-queue/:email_id",
    alias: "ignore_failed_email_queue_item_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string() }).passthrough(),
      },
      {
        name: "email_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/admin/failed-email-queue/:email_id",
    alias: "get_failed_email_queue_item_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "email_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: FailedEmailQueueItem,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/admin/failed-email-queue/:email_id.eml",
    alias: "get_failed_email_queue_item_content_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "email_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/admin/failed-email-queue/:email_id.html",
    alias: "get_failed_email_queue_item_content_html_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "email_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/admin/failed-email-queue/:email_id.txt",
    alias: "get_failed_email_queue_item_content_text_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "email_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/v1/admin/failed-email-queue/:email_id/make-alert",
    alias: "make_alert_from_failed_email_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "email_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/v1/admin/failed-email-queue/:email_id/make-case",
    alias: "make_case_from_failed_email_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "email_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/v1/admin/failed-email-queue/:email_id/reprocess",
    alias: "reprocess_failed_email_queue_item_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ reason: z.string() }).passthrough(),
      },
      {
        name: "email_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useAdminApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_API_URI, AdminEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WD-Account": accountContext.accountId,
        "X-WD-Email": userContext.apparentUserEmail,
      },
    },
  });
};

export const AlertsEndpoints = makeApi([
  {
    method: "get",
    path: "/v1/alerts",
    alias: "query_alerts",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "limit",
        type: "Query",
        schema: limit,
      },
      {
        name: "page_size",
        type: "Query",
        schema: limit,
      },
      {
        name: "sort",
        type: "Query",
        schema: sort,
      },
      {
        name: "filter",
        type: "Query",
        schema: filter,
      },
    ],
    response: z.array(Alert),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/v1/alerts",
    alias: "create_alert",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateAlert,
      },
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
    ],
    response: Alert,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/alerts/:alert_id",
    alias: "get_alert_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "alert_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Alert,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "patch",
    path: "/v1/alerts/:alert_id",
    alias: "update_alert_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateAlert,
      },
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "alert_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Alert,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/alerts/:alert_id/activity",
    alias: "get_alert_activity_by_alert_id",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "alert_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "limit",
        type: "Query",
        schema: limit,
      },
      {
        name: "page_size",
        type: "Query",
        schema: limit,
      },
      {
        name: "sort",
        type: "Query",
        schema: sort,
      },
      {
        name: "filter",
        type: "Query",
        schema: filter,
      },
    ],
    response: z.array(AlertActivity),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/alerts/:alert_id/activity/:activity_id/attachments",
    alias: "get_alert_activity_attachments",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "alert_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "activity_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/alerts/:alert_id/activity/:activity_id/attachments/:attachment_id",
    alias: "get_alert_activity_attachment_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "alert_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "activity_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "attachment_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useAlertsApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_API_URI, AlertsEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WD-Account": accountContext.accountId,
        "X-WD-Email": userContext.apparentUserEmail,
      },
    },
  });
};

export const Api_CredentialsEndpoints = makeApi([
  {
    method: "get",
    path: "/v1/api-credentials",
    alias: "get_api_credentials",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
    ],
    response: z.array(ApiCredential),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/v1/api-credentials",
    alias: "create_new_api_credential",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ name: z.string() }).passthrough(),
      },
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
    ],
    response: NewApiCredential,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "delete",
    path: "/v1/api-credentials/:api_credential_id",
    alias: "archive_api_credential",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "api_credential_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/api-credentials/:api_credential_id",
    alias: "get_api_credential",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "api_credential_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApiCredential,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "patch",
    path: "/v1/api-credentials/:api_credential_id",
    alias: "update_api_credential",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchApiCredential,
      },
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "api_credential_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ApiCredential,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/v1/api-credentials/:api_credential_id/regenerate-secret",
    alias: "regenerate_secret_for_api_credential",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "api_credential_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: NewApiCredential,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useApi_CredentialsApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_API_URI, Api_CredentialsEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WD-Account": accountContext.accountId,
        "X-WD-Email": userContext.apparentUserEmail,
      },
    },
  });
};

export const BrandsEndpoints = makeApi([
  {
    method: "get",
    path: "/v1/brands/self",
    alias: "v1_brands_self_get_v1_brands_self_get",
    requestFormat: "json",
    response: BrandResponse,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "patch",
    path: "/v1/brands/self",
    alias: "v1_brands_self_patch_v1_brands_self_patch",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BrandPatch,
      },
    ],
    response: BrandResponse,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "put",
    path: "/v1/brands/self",
    alias: "v1_brands_self_put_v1_brands_self_put",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: BrandPut,
      },
    ],
    response: BrandResponse,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useBrandsApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_API_URI, BrandsEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WD-Account": accountContext.accountId,
        "X-WD-Email": userContext.apparentUserEmail,
      },
    },
  });
};

export const CasesEndpoints = makeApi([
  {
    method: "get",
    path: "/v1/cases",
    alias: "query_cases",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "limit",
        type: "Query",
        schema: limit,
      },
      {
        name: "page_size",
        type: "Query",
        schema: limit,
      },
      {
        name: "sort",
        type: "Query",
        schema: sort,
      },
      {
        name: "filter",
        type: "Query",
        schema: filter,
      },
    ],
    response: z.array(Case),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/v1/cases",
    alias: "new_case",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateCase,
      },
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
    ],
    response: Case,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/cases/:case_id",
    alias: "get_case_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "case_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: Case,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/cases/:case_id/activity",
    alias: "query_case_activities",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "case_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "limit",
        type: "Query",
        schema: limit,
      },
      {
        name: "page_size",
        type: "Query",
        schema: limit,
      },
      {
        name: "sort",
        type: "Query",
        schema: sort,
      },
      {
        name: "filter",
        type: "Query",
        schema: filter,
      },
    ],
    response: z.array(CaseActivity),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/cases/:case_id/activity/:activity_id/attachments/:attachment_id",
    alias: "get_case_activity_attachment_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "case_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "activity_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "attachment_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useCasesApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_API_URI, CasesEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WD-Account": accountContext.accountId,
        "X-WD-Email": userContext.apparentUserEmail,
      },
    },
  });
};

export const Data_SheetsEndpoints = makeApi([
  {
    method: "get",
    path: "/v1/datasheet/:product.pdf",
    alias: "v1_datasheet_v1_datasheet__product__pdf_get",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "product",
        type: "Path",
        schema: z.string(),
      },
      {
        name: "brand",
        type: "Query",
        schema: filter,
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useData_SheetsApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_API_URI, Data_SheetsEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WD-Account": accountContext.accountId,
        "X-WD-Email": userContext.apparentUserEmail,
      },
    },
  });
};

export const EntitlementsEndpoints = makeApi([
  {
    method: "get",
    path: "/v1/entitlements/component-portals",
    alias:
      "v1_entitlements_componet_portals_v1_entitlements_component_portals_get",
    requestFormat: "json",
    response: z.void(),
  },
  {
    method: "get",
    path: "/v1/product-catalog/apps",
    alias: "v1_product_catalog_apps_v1_product_catalog_apps_get",
    requestFormat: "json",
    response: z.void(),
  },
]);

export const useEntitlementsApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_API_URI, EntitlementsEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WD-Account": accountContext.accountId,
        "X-WD-Email": userContext.apparentUserEmail,
      },
    },
  });
};

export const FilesEndpoints = makeApi([
  {
    method: "get",
    path: "/v1/files",
    alias: "get_directory_listing_by_path",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "path",
        type: "Query",
        schema: z.string().optional().default("/"),
      },
    ],
    response: DirectoryListing,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/v1/files",
    alias: "create_new_file",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateFile,
      },
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
    ],
    response: File,
    errors: [
      {
        status: 400,
        description: `Bad Request`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/files/categories",
    alias: "get_file_categories",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
    ],
    response: z.array(FileCategory),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/v1/files/categories",
    alias: "create_file_category",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
    ],
    response: z.array(FileCategory),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "delete",
    path: "/v1/files/categories/:file_category_id",
    alias: "delete_file_category",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "file_category_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: FileCategory,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/files/categories/:file_category_id",
    alias: "get_file_category",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "file_category_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: FileCategory,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "patch",
    path: "/v1/files/categories/:file_category_id",
    alias: "update_file_category",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "file_category_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: FileCategory,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/files/content",
    alias: "v1_files_content_v1_files_content_get",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "path",
        type: "Query",
        schema: z.string().optional().default("/"),
      },
    ],
    response: File,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/files/folder",
    alias: "get_folder_info_by_path",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "path",
        type: "Query",
        schema: z.string().optional().default("/"),
      },
    ],
    response: Folder,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/files/stats",
    alias: "get_stats",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
    ],
    response: FileStats,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "delete",
    path: "/v1/files/:file_id",
    alias: "delete_file",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "file_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/files/:file_id",
    alias: "get_file_info",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "file_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: File,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "patch",
    path: "/v1/files/:file_id",
    alias: "update_file_info",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "file_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: File,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/files/:file_id/content",
    alias: "get_file_content",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "file_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "download",
        type: "Query",
        schema: z.boolean().optional().default(false),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/files/:file_id/content-url",
    alias: "get_file_content_url",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "file_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "ttl",
        type: "Query",
        schema: z.number().int().gte(0).lte(3600).optional().default(60),
      },
      {
        name: "download",
        type: "Query",
        schema: z.boolean().optional().default(false),
      },
    ],
    response: z.object({ url: z.string() }).passthrough(),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/files/:file_id/versions",
    alias: "get_file_versions",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "file_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.array(FileVersion),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/v1/files/:file_id/versions",
    alias: "create_new_file_version",
    description: `Use this to upload a version of a file. The content of the file you want to upload should be the body of this request.`,
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "file_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "body",
        type: "Body",
        schema: z.instanceof(window.File),
      },
    ],
    response: FileVersionUploadResponse,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "delete",
    path: "/v1/files/:file_id/versions/:version_id",
    alias: "delete_file_version",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "file_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "version_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/files/:file_id/versions/:version_id",
    alias: "get_file_version_info",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "file_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "version_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({ id: z.string().uuid() }).passthrough(),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/files/:file_id/versions/:version_id/content",
    alias: "get_file_version_content",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "file_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "version_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "download",
        type: "Query",
        schema: z.boolean().optional().default(false),
      },
    ],
    response: z.array(FileVersion),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/files/:file_id/versions/:version_id/content-url",
    alias: "get_file_version_content_url",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "file_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "version_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "ttl",
        type: "Query",
        schema: z.number().int().gte(0).lte(3600).optional().default(60),
      },
      {
        name: "download",
        type: "Query",
        schema: z.boolean().optional().default(false),
      },
    ],
    response: z.object({ url: z.string() }).passthrough(),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useFilesApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_API_URI, FilesEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WD-Account": accountContext.accountId,
        "X-WD-Email": userContext.apparentUserEmail,
      },
    },
  });
};

export const JobsEndpoints = makeApi([
  {
    method: "get",
    path: "/v1/jobs/:job_id",
    alias: "get_status_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "job_id",
        type: "Path",
        schema: z.string(),
      },
    ],
    response: JobStatus,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useJobsApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_API_URI, JobsEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WD-Account": accountContext.accountId,
        "X-WD-Email": userContext.apparentUserEmail,
      },
    },
  });
};

export const NodesEndpoints = makeApi([
  {
    method: "get",
    path: "/v1/nodes",
    alias: "v1_nodes_v1_nodes_get",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
    ],
    response: z.array(NodeListItem),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/nodes/:node_id",
    alias: "v1_nodes_node_id_v1_nodes__node_id__get",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "node_id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: NodeDataPrint,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useNodesApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_API_URI, NodesEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WD-Account": accountContext.accountId,
        "X-WD-Email": userContext.apparentUserEmail,
      },
    },
  });
};

export const Org_LinksEndpoints = makeApi([
  {
    method: "get",
    path: "/v1/org-links",
    alias: "get_org_links",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
    ],
    response: z.array(OrgLink),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useOrg_LinksApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_API_URI, Org_LinksEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WD-Account": accountContext.accountId,
        "X-WD-Email": userContext.apparentUserEmail,
      },
    },
  });
};

export const SystemsEndpoints = makeApi([
  {
    method: "get",
    path: "/v1/systems",
    alias: "v1_systems_v1_systems_get",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
    ],
    response: z.array(SystemListItem),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/systems/:system_id",
    alias: "v1_systems_system_id_v1_systems__system_id__get",
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
      {
        name: "system_id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: SystemDataPrint,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useSystemsApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_API_URI, SystemsEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WD-Account": accountContext.accountId,
        "X-WD-Email": userContext.apparentUserEmail,
      },
    },
  });
};

export const UsersEndpoints = makeApi([
  {
    method: "get",
    path: "/v1/user/me",
    alias: "v1_user_me_v1_user_me_get",
    requestFormat: "json",
    response: User,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/user/:user_id",
    alias: "v1_user_user_id_v1_user__uuid_user_id__get",
    requestFormat: "json",
    parameters: [
      {
        name: "user_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.object({}).partial().passthrough(),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/users",
    alias: "v1_users_v1_users_get",
    requestFormat: "json",
    response: User,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
    ],
  },
]);

export const useUsersApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_API_URI, UsersEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WD-Account": accountContext.accountId,
        "X-WD-Email": userContext.apparentUserEmail,
      },
    },
  });
};

export const UXEndpoints = makeApi([
  {
    method: "get",
    path: "/v1/ux/accountstree",
    alias: "v1_ux_account_tree_v1_ux_accountstree_get",
    description: `Gets an account tree. Used by WD Portal for Account Dropdown`,
    requestFormat: "json",
    parameters: [
      {
        name: "x-wd-account",
        type: "Header",
        schema: filter,
      },
    ],
    response: z.array(TreeNode),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/v1/ux/strev-token",
    alias: "get_strev_token",
    requestFormat: "json",
    response: z.object({ token: z.string() }).passthrough(),
  },
]);

export const useUXApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_API_URI, UXEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WD-Account": accountContext.accountId,
        "X-WD-Email": userContext.apparentUserEmail,
      },
    },
  });
};
